import { useUpdateApplication, useUpdateDefaultApplication } from '../../hooks/queries';
import { usePaneManagementStore } from '../PaneManager/PaneStore';
import BaseCard, { CardMenu } from './BaseCard';
import { useAnalytics } from '../../v2/third-party-integration/amplitude';
import { AMPLITUDE_EVENTS } from '../../constants';
import { useState } from 'react';
import SetStartupAppDialog from '../Dialogs/SetStartupAppDialog';
import { useUnifyProfileStore, useApplicationStore, useStartupToastStore, useAllApplicationsStore, useWorkflowsStore } from '../../Store';
import { useTranslation } from 'react-i18next';
import { useFeatureFlag } from '@harnessio/ff-react-client-sdk';

interface Props {
  id: string;
  isEnabled: boolean;
  isFavorite?: boolean;
  description: string;
  name: string;
  iconColor: string;
  iconText: string;
  url: string;
  onCardOpen: Function;
  onCdkuOpen?: Function;
  cdkuUrl?: string;
  menu?: CardMenu[];
  menuHandler?: Function;
  isRestricted?: boolean;
  restrictedTooltipMsg?: string;
  openInNewTab?: boolean;
  showNewIndicator?: boolean;
  isDefault?: boolean;
  hideStartupOption?: boolean;
  warningMessage?: string;
  requiredOS?: string[];
  isCustomLink?: boolean;
}

export default function ApplicationCard({
  id,
  isFavorite,
  isEnabled,
  description,
  onCardOpen,
  name,
  iconText,
  iconColor,
  onCdkuOpen,
  cdkuUrl,
  menu,
  menuHandler,
  isRestricted,
  restrictedTooltipMsg,
  url,
  openInNewTab,
  showNewIndicator,
  isDefault = false,
  hideStartupOption = false,
  warningMessage,
  requiredOS,
  isCustomLink=false,
}: Props) {
  const { mutate } = useUpdateApplication();
  const { mutate: setDefaultAppMutation } = useUpdateDefaultApplication();
  const setApplicationCardMousePosition = usePaneManagementStore(state => state.setApplicationCardMousePosition);
  const [showStartupDialog, setShowStartupDialog] = useState(false);
  const { unifyProfile } = useUnifyProfileStore();
  const applications = useApplicationStore((state: any) => state.applications);
  const allApplications = useAllApplicationsStore((state: any) => state.applications);
  const workflows = useWorkflowsStore((state: any) => state.workflows);
  const { showStartupToast } = useStartupToastStore();
  const { t } = useTranslation();
  const startupAppFlag = useFeatureFlag('UNIFY_STARTUP_APP');

  const currentDefaultName = (() => {
    if (!unifyProfile?.apps) return '';

    // Check for existing default app (different from this one)
    const defaultApp = unifyProfile.apps.find(
      (app: any) =>
        (app.isDefaultApp === true ) &&
        (app.appId ) !== id
    );
    if (defaultApp) {
      const appId = defaultApp.appId;
      const appDetails =
        applications?.find((a: any) => a.id === appId) ||
        allApplications?.flat().find((a: any) => a.appId === appId);
      return appDetails?.name || '';
    }

    // Check for existing default workflow
    const defaultWorkflow = unifyProfile.workflows?.find(
      (wf: any) => wf.isDefaultWorkflow === true
    );
    if (defaultWorkflow) {
      const wfId = defaultWorkflow.workflowId;
      return workflows?.find((wf: any) => wf.id === wfId)?.name || '';
    }

    return '';
  })();

  const getDialogScenario = (): 'set' | 'remove' | 'update' => {
    if (isDefault) return 'remove';
    if (currentDefaultName) return 'update';
    return 'set';
  };

  const onFavoriteClick = () => {
    mutate({ id, isFavorite: !isFavorite });
    trackFavoriteChange(!isFavorite);
  };

  const setDefaultApp = () => {
    setShowStartupDialog(true);
  };

  const confirmDefaultApp = () => {
    const settingAsDefault = !isDefault;
    setDefaultAppMutation(
      { id, isDefault: settingAsDefault },
      {
        onSuccess: () => {
          trackEvent(AMPLITUDE_EVENTS.SET_STARTUP, {
            'Application': name,
            'Action': settingAsDefault ? 'Set' : 'Remove',
          });
          if (settingAsDefault) {
            showStartupToast(t('baseCard.startupApp.toastSet', { appName: name }), 'positive');
          } else {
            showStartupToast(t('baseCard.startupApp.toastRemove', { appName: name }), 'positive');
          }
        },
        onError: () => {
          showStartupToast(t('baseCard.startupApp.toastError'), 'negative');
        },
      }
    );
    setShowStartupDialog(false);
  };

  const trackFavoriteChange = (isFavorite: boolean) => {
    isFavorite &&
      trackEvent(AMPLITUDE_EVENTS.OPEN_APPLICATION, {
        'Favorite Application': name,
      });
  };

  const { trackEvent } = useAnalytics();
  const onCardClick = (e:MouseEvent) => {

    setApplicationCardMousePosition({clickX:e.clientX ,clickY:e.clientY});
    if (!isCustomLink) {
      trackEvent(AMPLITUDE_EVENTS.OPEN_APPLICATION, {
        Application: name,
      });
    }
    onCardOpen(id);
  };

  return (
    <>
      <BaseCard
        iconColor={iconColor}
        iconText={iconText}
        onOpenClick={(e) =>  onCardClick(e as unknown as MouseEvent)}
        description={description}
        name={name}
        isFavorite={isFavorite}
        isEnabled={isEnabled}
        onFavoriteClick={onFavoriteClick}
        isIconRequired={true}
        showDescription={false}
        onCdkuOpen={onCdkuOpen}
        cdkuUrl={cdkuUrl}
        menu={menu}
        menuHandler={menuHandler}
        isRestricted={isRestricted}
        restrictedTooltipMsg={restrictedTooltipMsg}
        url={url}
        openInNewTab={openInNewTab}
        id={id}
        showNewIndicator={showNewIndicator as boolean}
        warningMessage={warningMessage}
        requiredOS={requiredOS}
        setDefaultApp={setDefaultApp}
        isDefault={isDefault}
        showStartupOption={!hideStartupOption && !!url && url.startsWith('http') && Boolean(startupAppFlag)}
      />
      <SetStartupAppDialog
        isOpen={showStartupDialog}
        appName={name}
        scenario={getDialogScenario()}
        previousDefaultAppName={currentDefaultName}
        onConfirm={confirmDefaultApp}
        onDismiss={() => setShowStartupDialog(false)}
      />
    </>
  );
}